import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import Filters from "src/pages/Reports/WorkPlaceReport/components/filters/Filters";
import { useEffect, useMemo, useRef, useState } from "react";
import Header from "src/pages/Reports/WorkPlaceReport/components/Header";
import CollapseTable from "src/pages/Reports/WorkPlaceReport/components/CollapseTable";
import { DocumentType, UserType, WorkplaceType } from "src/pages/Reports/WorkPlaceReport/types";
import Table from "src/pages/Reports/WorkPlaceReport/components/Table";
import Breadcrumbs from "src/pages/Reports/WorkPlaceReport/components/Breadcrumbs";
import { createPortal } from "react-dom";
import FavoriteFiltersModal from "src/pages/Reports/WorkPlaceReport/components/FiltersModal";
import { SubmitWorkPlaceFiltersType } from "../types";
import {
  collectionsListName,
  departmentsListName,
  dependenciesListName,
  favoriteFiltersListName,
  ListFieldsType,
  MDWorkstationListName,
  myDocumentsListName,
} from "src/api/config";
import { getItemsRequest, FetchOptions } from "src/modules/spItems/api";
import {
  modifiedItemsState,
  selectAddStatus,
  selectDeleteStatus,
  selectGetStatus,
  selectItems,
  selectJustAddedItem,
  selectJustDeletedItem,
  selectUpdateStatus,
} from "src/modules/spItems/selectors";
import { addItem, deleteItem, getItems, updateItem } from "src/modules/spItems/actions";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectCurrentUser } from "src/modules/users/selector";
import BundlesModal from "./components/BundlesModal";
import { notificationsListName } from "src/api/config/notifications.config";
import { docStatuses, notificationStatuses } from "src/api/config/systemSwitch";
import { CurrentUserType } from "src/modules/users/types";
import { chunkArray } from "src/utils/array.utils";

const getFamiliarizationPercent = (familiarizedUsers: number, nonFamiliarizedUsers: number) => {
  const totalUsers = familiarizedUsers + nonFamiliarizedUsers;
  return Math.round((familiarizedUsers / totalUsers) * 100);
};

function getPersonalNumber(employee: any) {
  if (employee.Metadata.UserProfileProperties) {
    return (
      employee.Metadata.UserProfileProperties.find((u: any) => u.Key === "abiGlobalID")?.Value ||
      employee.Metadata.UserProfileProperties.find((u: any) => u.Key === "EmployeeNumber")?.Value ||
      employee.Metadata.UserProfileProperties.find((u: any) => u.Key === "abiPersonnelNumber")?.Value ||
      employee.Metadata.UserProfileProperties.find((u: any) => u.Key === "UserName")?.Value ||
      "Supply Number"
    );
  } else {
    return (
      employee.Metadata.personalNumber ||
      employee.Metadata.abiGlobalID ||
      employee.Metadata.EmployeeNumber ||
      employee.Metadata.abiPersonnelNumber ||
      "Supply Number"
    );
  }
}

function getName(employee: any) {
  if (!employee.Name) {
    return "Supply Portal";
  }

  let newName = employee.Name.split(" ");
  if (newName.length > 0) {
    newName = newName[0] + " " + newName[1];
  }

  return newName + " (" + getPersonalNumber(employee) + ")";
}
function getImg(employee: any) {
  return employee.Metadata.PictureUrl || "";
}
function getShortName(employee: any) {
  if (employee.Name) {
    const nameArr = employee.Name.split(" ");
    if (nameArr.length > 0) {
      return nameArr[0][0] + nameArr[1][0];
    } else return "SP";
  }
}

const NOTIFICATIONS_DOCUMENT_ID_CHUNK_SIZE = 50;

type FamiliarizationBuckets = Pick<
  DocumentType,
  "familiarizedUsers" | "nonFamiliarizedUsers" | "canceledUsers" | "familiarizationPercent"
>;

const createEmptyFamiliarizationBuckets = (): FamiliarizationBuckets => ({
  familiarizedUsers: [],
  nonFamiliarizedUsers: [],
  canceledUsers: [],
  familiarizationPercent: 0,
});

const finalizeFamiliarizationPercent = (buckets: FamiliarizationBuckets) => {
  buckets.familiarizationPercent = getFamiliarizationPercent(
    buckets.familiarizedUsers.length,
    buckets.nonFamiliarizedUsers.length + buckets.canceledUsers.length,
  );
};

const appendNotificationToBuckets = (
  buckets: FamiliarizationBuckets,
  item: ListFieldsType[typeof notificationsListName],
) => {
  if (item.VersionDocument || !item.Employee) {
    return;
  }

  const user: UserType = {
    id: item.Employee.ID,
    name: getName(item.Employee),
    img: getImg(item.Employee),
    email: item.Employee.Email,
    shortName: getShortName(item.Employee),
  };

  if (item.Completed) {
    buckets.familiarizedUsers.push(user);
  } else if (item.Title.includes("Отклонено")) {
    buckets.canceledUsers.push(user);
  } else {
    buckets.nonFamiliarizedUsers.push(user);
  }
};

async function fetchNotificationsByDocumentIds(
  documentIds: number[],
  notificationFilters: Record<string, unknown>,
  isCancelled: () => boolean,
): Promise<Map<number, FamiliarizationBuckets>> {
  const byDocumentId = new Map<number, FamiliarizationBuckets>();
  documentIds.forEach((id) => byDocumentId.set(id, createEmptyFamiliarizationBuckets()));

  if (documentIds.length === 0) {
    return byDocumentId;
  }

  const uniqueDocumentIds = [...new Set(documentIds)];
  const documentIdChunks = chunkArray(uniqueDocumentIds, NOTIFICATIONS_DOCUMENT_ID_CHUNK_SIZE);

  const fetchChunk = async (documentIdsChunk: number[]) => {
    let nextPage: number | null = null;

    for (;;) {
      if (isCancelled()) {
        return;
      }

      const requestOptions: FetchOptions<typeof notificationsListName> = {
        listName: notificationsListName,
        filters: {
          ...notificationFilters,
          Document: documentIdsChunk,
          NotificationType: [
            notificationStatuses.acquainted,
            notificationStatuses.acquaintedTest,
            notificationStatuses.familiarization,
          ],
        },
        rowLimit: 5000,
        viewFields: ["Title", "Completed"],
        populateFields: ["Employee", "VersionDocument", "Document"],
        switchViewFields: true,
        switchPopulate: false,
      };
      if (nextPage != null) {
        requestOptions.nextPage = nextPage;
      }

      const response = await getItemsRequest(requestOptions);

      const notifications = modifiedItemsState(response.items, notificationsListName);

      notifications.forEach((item) => {
        const documentId = item.Document?.ID;
        if (documentId == null) {
          return;
        }

        let buckets = byDocumentId.get(documentId);
        if (!buckets) {
          buckets = createEmptyFamiliarizationBuckets();
          byDocumentId.set(documentId, buckets);
        }
        appendNotificationToBuckets(buckets, item);
      });

      if (response.nextPage == null) {
        break;
      }
      nextPage = response.nextPage;
    }
  };

  await Promise.all(documentIdChunks.map((chunk) => fetchChunk(chunk)));

  byDocumentId.forEach((buckets) => finalizeFamiliarizationPercent(buckets));
  return byDocumentId;
}

function getUserAdminCollections(currentUser: CurrentUserType): number[] {
  if (currentUser.id > 0) {
    const defaultCollectionsIds = new Set<number>();

    if (Array.isArray(currentUser.isCollectionAdmin) && currentUser.isCollectionAdmin.length > 0) {
      currentUser.isCollectionAdmin.forEach((id) => defaultCollectionsIds.add(id));
    }
    if (Array.isArray(currentUser.isCollectionEditor) && currentUser.isCollectionEditor.length > 0) {
      currentUser.isCollectionEditor.forEach((id) => defaultCollectionsIds.add(id));
    }

    return [...defaultCollectionsIds.values()];
  }
  return [];
}

const WorkPlaceReport = () => {
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => selectCurrentUser(state));
  const favoriteFiltersUpdatedStatus = useAppSelector((state) => selectUpdateStatus(state, favoriteFiltersListName));
  const favoriteFiltersDeletedStatus = useAppSelector((state) => selectDeleteStatus(state, favoriteFiltersListName));
  const favoriteFiltersAddedStatus = useAppSelector((state) => selectAddStatus(state, favoriteFiltersListName));
  const favoriteFiltersData = useAppSelector((state) =>
    selectItems(state, favoriteFiltersListName),
  ) as ListFieldsType[typeof favoriteFiltersListName][];

  const [workplaces, setWorkplaces] = useState<(WorkplaceType & { collapsed: boolean })[]>([]);
  const [collapseTableChecked, setCollapseTableChecked] = useState<number | null>(0);
  const [favoriteFilterModalOpened, setFavoriteFilterModalOpened] = useState(false);
  const [bundlesModalOpened, setBundlesModalOpened] = useState(false);
  const [workstationsLoading, setWorkstationsLoading] = useState(true);
  const [filtersReady, setFiltersReady] = useState(false);
  const [submitFilters, setSubmitFilters] = useState<SubmitWorkPlaceFiltersType>({
    query: "",
    collections: [],
    departments: [],
    workstations: [],
    employees: [],
  });

  const [isFavorite, setIsFavorite] = useState(false);
  const [currentFavoriteId, setCurrentFavoriteId] = useState<string | null>(null);
  const [firstSelectedFilter, setFirstSelectedFilter] = useState<
    "collections" | "departments" | "workstations" | "employees" | null
  >(null);

  const changeFirstSelectedFilter = (value: "collections" | "departments" | "workstations" | "employees" | null) => {
    setFirstSelectedFilter(value);
  };

  const onExpandAll = () => {
    setWorkplaces((prev) => prev.map((item) => ({ ...item, collapsed: false })));
  };

  const onCollapseAll = () => {
    setWorkplaces((prev) => prev.map((item) => ({ ...item, collapsed: true })));
  };

  const changeSubmitFilters = (e: SubmitWorkPlaceFiltersType) => {
    setSubmitFilters(e);
  };

  const resetSubmitFilters = () => {
    setSubmitFilters({
      query: "",
      collections: [],
      departments: [],
      workstations: [],
      employees: [],
    });
  };

  const toggleCollapsed = (id: string) => {
    setWorkplaces((prev) => {
      const newArr = [...prev];
      const elToToggle = newArr.find((item) => item.id === id);
      if (elToToggle) {
        elToToggle.collapsed = !elToToggle.collapsed;
      }
      return newArr;
    });
  };

  const saveFilter = ({ name, isDefault }: { name: string; isDefault: boolean }) => {
    const defaultFilters = favoriteFiltersData.filter((item) => item.isDefault === true && item.Type === "workplace");

    if (isDefault && defaultFilters.length > 0) {
      // Снимаем дефолт у всех других, которые isDefault=true
      defaultFilters.forEach((item) => {
        dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: item.ID, isDefault: false } }));
      });
    }

    const requestSubmitFilters = {
      firstSelectedFilter: firstSelectedFilter,
      filters: submitFilters,
    };

    dispatch(
      addItem({
        listName: favoriteFiltersListName,
        body: {
          Title: name,
          DocumentsFilter: JSON.stringify(requestSubmitFilters),
          Type: "workplace",
          isDefault: isDefault,
        },
      }),
    );
    setFavoriteFilterModalOpened(false);
  };

  const deleteCurrentFilter = () => {
    if (currentFavoriteId) {
      dispatch(
        deleteItem({
          listName: favoriteFiltersListName,
          ID: currentFavoriteId,
        }),
      );
      setIsFavorite(false);
      setCurrentFavoriteId(null);
    }
  };

  const onSearch = (query: string) => {
    changeSubmitFilters({ ...submitFilters, query: query });
  };

  useEffect(() => {
    // `cancelled` is set to true by the cleanup function when filters change
    // while a previous async fetch is still in flight. Every setState call
    // is guarded by this flag so stale results never overwrite newer ones.
    let cancelled = false;

    async function fetchWorkplaces() {
      if (!filtersReady) return;

      const hasActiveFilters =
        submitFilters.collections.length > 0 ||
        submitFilters.departments.length > 0 ||
        submitFilters.workstations.length > 0 ||
        submitFilters.employees.length > 0 ||
        (submitFilters.query || "").trim() !== "";

      if (!hasActiveFilters) {
        if (!cancelled) {
          setWorkplaces([]);
          setWorkstationsLoading(false);
        }
        return;
      }

      try {
        if (!cancelled) setWorkstationsLoading(true);

        const workstationsFilters: Record<string, number[]> = {};
        if (submitFilters.workstations.length > 0) {
          workstationsFilters.ID = submitFilters.workstations.map((item) => Number(item.id));
        }

        if (submitFilters.employees.length > 0) {
          workstationsFilters.User = submitFilters.employees.map((item) => Number(item.id));
        }

        if (submitFilters.collections.length > 0) {
          workstationsFilters.Location = submitFilters.collections.map((item) => Number(item.id));
        }
        if (submitFilters.departments.length > 0) {
          workstationsFilters.Department = submitFilters.departments.map((item) => Number(item.id));
        }
        const workstationsResponse = await getItemsRequest({
          listName: MDWorkstationListName,
          filters: workstationsFilters,
          viewFields: ["ID", "Workstation"],
          populateFields: ["User"],
          switchViewFields: true,
          switchPopulate: false,
        });

        if (cancelled) return;

        if (workstationsResponse.items.length === 0) {
          setWorkplaces([]);
          setWorkstationsLoading(false);
          return;
        }

        if (workstationsResponse.items.length > 0) {
          const workstations = modifiedItemsState(workstationsResponse.items, MDWorkstationListName);
          const workplacesData: Record<string, WorkplaceType> = workstations.reduce(
            (acc, workstation) => {
              acc[workstation.ID.toString()] = {
                id: workstation.ID.toString(),
                users: workstation.User?.map((u) => ({ ID: u.ID, Email: u.Email })) || [],
                title: workstation.Workstation,
                familiarizationPercent: 0,
                documents: [],
              };

              return acc;
            },
            {} as Record<string, WorkplaceType>,
          );

          // Status (а не CardStatus) есть в listFields → тип LOOKUP → массив сериализуется как $in, не $eq
          const docFilters: Record<string, unknown> = { Status: [docStatuses.review, docStatuses.public] };

          if (submitFilters.query.length > 0) {
            docFilters.Title = submitFilters.query + "*";
          }

          const documentsResponse = await getItemsRequest({
            listName: myDocumentsListName,
            filters: {
              ...docFilters,
              WorkPlaces: Object.keys(workplacesData),
            },
            viewFields: ["ID", "Title", "Code", "Index", "Ver", "Critical"],
            populateFields: ["WorkPlaces"],
            switchPopulate: false,
            switchViewFields: true,
          });

          if (cancelled) return;

          const notificationsFilters: Record<string, unknown> = {};
          if (submitFilters.employees.length > 0) {
            notificationsFilters.Employee = submitFilters.employees.map((item) => Number(item.id));
          }

          if (documentsResponse.items.length > 0) {
            const documents = modifiedItemsState(documentsResponse.items, myDocumentsListName);
            const documentIds = documents.map((document) => document.ID);
            const notificationsByDocumentId = await fetchNotificationsByDocumentIds(
              documentIds,
              notificationsFilters,
              () => cancelled,
            );

            if (cancelled) return;

            for (const document of documents) {
              const mainDocNotifications =
                notificationsByDocumentId.get(document.ID) ?? createEmptyFamiliarizationBuckets();

              document.WorkPlaces.forEach((workplace) => {
                const workplaceData = workplacesData[workplace.ID.toString()];
                if (!workplaceData) return;

                const workplaceUserEmails = new Set(
                  workplaceData.users.map((u) => u.Email?.toLowerCase().trim()).filter(Boolean),
                );

                const familiarizedUsers = mainDocNotifications.familiarizedUsers.filter(
                  (u) => u.email && workplaceUserEmails.has(u.email.toLowerCase().trim()),
                );
                const nonFamiliarizedUsers = mainDocNotifications.nonFamiliarizedUsers.filter(
                  (u) => u.email && workplaceUserEmails.has(u.email.toLowerCase().trim()),
                );

                const canceledUsers = mainDocNotifications.canceledUsers.filter(
                  (u) => u.email && workplaceUserEmails.has(u.email.toLowerCase().trim()),
                );

                const fCount = familiarizedUsers.length;
                const nfCount = nonFamiliarizedUsers.length + canceledUsers.length;

                workplaceData.documents.push({
                  id: document.ID.toString(),
                  name: document.Title,
                  code: `${document.Code}.${document.Index}`,
                  version: document.Ver || 1,
                  critical: document.Critical,
                  familiarizationPercent: fCount + nfCount === 0 ? 0 : getFamiliarizationPercent(fCount, nfCount),
                  familiarizedUsers,
                  nonFamiliarizedUsers,
                  canceledUsers,
                });
              });
            }
          }

          if (cancelled) return;

          // При выборе сотрудников: оставляем только документы, где у выбранного есть уведомление.
          // Дополнительно — обрезаем списки пользователей до выбранных и пересчитываем процент.
          if (submitFilters.employees.length > 0) {
            const selectedEmails = new Set(
              submitFilters.employees.map((e) => e.email?.toLowerCase().trim()).filter(Boolean),
            );
            const documentHasSelectedEmployee = (doc: DocumentType) =>
              doc.familiarizedUsers.some((u) => u.email && selectedEmails.has(u.email.toLowerCase().trim())) ||
              doc.nonFamiliarizedUsers.some((u) => u.email && selectedEmails.has(u.email.toLowerCase().trim()));

            for (const workplace of Object.values(workplacesData)) {
              workplace.documents = workplace.documents.filter(documentHasSelectedEmployee);

              for (const doc of workplace.documents) {
                doc.familiarizedUsers = doc.familiarizedUsers.filter(
                  (u) => u.email && selectedEmails.has(u.email.toLowerCase().trim()),
                );
                doc.nonFamiliarizedUsers = doc.nonFamiliarizedUsers.filter(
                  (u) => u.email && selectedEmails.has(u.email.toLowerCase().trim()),
                );
                doc.canceledUsers = doc.canceledUsers.filter(
                  (u) => u.email && selectedEmails.has(u.email.toLowerCase().trim()),
                );

                const fCount = doc.familiarizedUsers.length;
                const nfCount = doc.nonFamiliarizedUsers.length + doc.canceledUsers.length;
                doc.familiarizationPercent = fCount + nfCount === 0 ? 0 : getFamiliarizationPercent(fCount, nfCount);
              }
            }
          }

          // Убираем рабочие места без документов только при активных фильтрах; без фильтров показываем все РМ, даже пустые
          if (hasActiveFilters) {
            const emptyWorkplaceIds = Object.entries(workplacesData)
              .filter(([, wp]) => wp.documents.length === 0)
              .map(([id]) => id);
            emptyWorkplaceIds.forEach((id) => delete workplacesData[id]);
          }

          // Общий процент ознакомления по рабочему месту:
          // считаем среднее значение процента ознакомления по ВСЕМ документам рабочего места
          // (документ без уведомлений даёт 0% и участвует в количестве документов)
          for (const workplace of Object.values(workplacesData)) {
            const docsCount = workplace.documents.length;
            if (docsCount === 0) {
              workplace.familiarizationPercent = 0;
              continue;
            }

            let totalPercent = 0;

            for (const doc of workplace.documents) {
              const familiarizedCount = doc.familiarizedUsers.length;
              const nonFamiliarizedCount = doc.nonFamiliarizedUsers.length + doc.canceledUsers.length;
              const docTotal = familiarizedCount + nonFamiliarizedCount;

              const docPercent =
                docTotal === 0
                  ? 0
                  : doc.familiarizationPercent > 0
                    ? doc.familiarizationPercent
                    : getFamiliarizationPercent(familiarizedCount, nonFamiliarizedCount);

              totalPercent += docPercent;
            }

            workplace.familiarizationPercent = Math.round(totalPercent / docsCount);
          }

          if (!cancelled) {
            const workplaces = Object.values(workplacesData).map((item) => ({ ...item, collapsed: false }));
            setWorkplaces(workplaces);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setWorkstationsLoading(false);
      }
    }

    fetchWorkplaces();

    return () => {
      cancelled = true;
    };
  }, [filtersReady, JSON.stringify(submitFilters)]);

  useEffect(() => {
    if (workplaces.every((item) => !item.collapsed)) {
      setCollapseTableChecked(0);
    } else if (workplaces.every((item) => item.collapsed)) {
      setCollapseTableChecked(1);
    } else {
      setCollapseTableChecked(null);
    }
  }, [workplaces]);

  useEffect(() => {
    if (user.email && user.id) {
      dispatch(getItems({ listName: favoriteFiltersListName, filters: { Author: user.id, Type: "workplace" } }));
    }
  }, [user.email]);

  useEffect(() => {
    if (
      favoriteFiltersUpdatedStatus === "SUCCESS" ||
      favoriteFiltersDeletedStatus === "SUCCESS" ||
      favoriteFiltersAddedStatus === "SUCCESS"
    ) {
      dispatch(getItems({ listName: favoriteFiltersListName, filters: { Author: user.id, Type: "workplace" } }));
    }
  }, [favoriteFiltersUpdatedStatus, favoriteFiltersDeletedStatus, favoriteFiltersAddedStatus]);

  const isFiltersEmpty = !(
    submitFilters.collections.length > 0 ||
    submitFilters.departments.length > 0 ||
    submitFilters.workstations.length > 0 ||
    submitFilters.employees.length > 0 ||
    submitFilters.query !== ""
  );

  return (
    <>
      <FlexRow className="scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] h-screen overflow-y-auto">
        <FlexCol
          className="bg-white sticky-custom border-r w-80 text-[14px] text-[#8899A8] relative
        scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB] max-h-screen max-w-[320px] overflow-y-auto"
        >
          <Filters
            submitFilters={submitFilters}
            changeSubmitFilters={changeSubmitFilters}
            resetSubmitFilters={resetSubmitFilters}
            isFiltersEmpty={isFiltersEmpty}
            firstSelectedFilter={firstSelectedFilter}
            changeFirstSelectedFilter={changeFirstSelectedFilter}
          />
        </FlexCol>

        <FlexCol className="grow w-[calc(100%-320px)]">
          <FlexRow className="px-7.5 h-[76px] border-b justify-between items-center shrink-0">
            <Breadcrumbs
              isFavorite={isFavorite}
              openFiltersModal={() => setFavoriteFilterModalOpened(true)}
              onDeleteFilter={deleteCurrentFilter}
              setBundlesModalOpened={setBundlesModalOpened}
              noFiltersSelected={isFiltersEmpty}
            />
          </FlexRow>
          <FlexCol className="px-7.5">
            <Header
              loading={workstationsLoading}
              searchQuery={submitFilters.query}
              onSearch={onSearch}
              workplaces={workplaces}
            />
            <CollapseTable
              expandAll={onExpandAll}
              collapseAll={onCollapseAll}
              checkedOption={collapseTableChecked}
              setCheckedOption={setCollapseTableChecked}
            />
            <Table workplaces={workplaces} toggleCollapsed={toggleCollapsed} loading={workstationsLoading} />
          </FlexCol>
        </FlexCol>
      </FlexRow>
      {createPortal(
        <FavoriteFiltersModal
          closeModal={() => setFavoriteFilterModalOpened(false)}
          opened={favoriteFilterModalOpened}
          saveFilter={saveFilter}
        />,
        document.body,
      )}
      <FavoriteFiltersListModal
        submitFilters={submitFilters}
        changeSubmitFilters={changeSubmitFilters}
        resetSubmitFilters={resetSubmitFilters}
        setCurrentFavoriteId={setCurrentFavoriteId}
        setIsFavorite={setIsFavorite}
        currentFavoriteId={currentFavoriteId}
        bundlesModalOpened={bundlesModalOpened}
        setBundlesModalOpened={setBundlesModalOpened}
        setFiltersReady={setFiltersReady}
        changeFirstSelectedFilter={changeFirstSelectedFilter}
      />
    </>
  );
};

export default WorkPlaceReport;

const FavoriteFiltersListModal = ({
  submitFilters,
  changeSubmitFilters,
  resetSubmitFilters,
  setCurrentFavoriteId,
  setIsFavorite,
  currentFavoriteId,
  bundlesModalOpened,
  setBundlesModalOpened,
  setFiltersReady,
  changeFirstSelectedFilter,
}: {
  submitFilters: SubmitWorkPlaceFiltersType;
  changeSubmitFilters: (filters: SubmitWorkPlaceFiltersType) => void;
  resetSubmitFilters: () => void;
  setCurrentFavoriteId: (id: string | null) => void;
  setIsFavorite: (isFavorite: boolean) => void;
  currentFavoriteId: string | null;
  bundlesModalOpened: boolean;
  setBundlesModalOpened: (opened: boolean) => void;
  setFiltersReady: (ready: boolean) => void;
  changeFirstSelectedFilter: (value: "collections" | "departments" | null) => void;
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => selectCurrentUser(state));
  const favoriteFiltersData = useAppSelector((state) => selectItems(state, favoriteFiltersListName));
  const favoriteFiltersGetStatus = useAppSelector((state) => selectGetStatus(state, favoriteFiltersListName));
  const favoriteFiltersUpdatedStatus = useAppSelector((state) => selectUpdateStatus(state, favoriteFiltersListName));
  const favoriteFiltersAdded = useAppSelector((state) => selectJustAddedItem(state, favoriteFiltersListName));
  const favoriteFiltersDeleted = useAppSelector((state) => selectJustDeletedItem(state, favoriteFiltersListName));

  const departmentsData = useAppSelector((state) => selectItems(state, departmentsListName));
  const departmentsGetStatus = useAppSelector((state) => selectGetStatus(state, departmentsListName));

  const defaultFilterApplied = useRef(false);

  // Мемоизируем сериализованное значение фильтров для правильной работы useEffect
  const submitFiltersKey = useMemo(() => {
    return JSON.stringify({
      query: submitFilters.query,
      collections: submitFilters.collections.map((c) => c.id),
      departments: submitFilters.departments.map((d) => d.id),
      workstations: submitFilters.workstations.map((w) => w.id),
      employees: submitFilters.employees.map((e) => e.id),
    });
  }, [JSON.stringify(submitFilters)]);

  // Функция для нормализованного сравнения фильтров
  const areFiltersEqual = (filter1: SubmitWorkPlaceFiltersType, filter2: SubmitWorkPlaceFiltersType): boolean => {
    // Извлекаем только ID из объектов для сравнения
    const getIds = (arr: any[]): number[] => {
      if (!arr || arr.length === 0) return [];
      return arr.map((item) => item.id || item.ID).sort((a, b) => a - b);
    };

    const ids1 = {
      query: filter1.query,
      collections: getIds(filter1.collections),
      departments: getIds(filter1.departments),
      workstations: getIds(filter1.workstations),
      employees: getIds(filter1.employees),
    };

    const ids2 = {
      query: filter2.query,
      collections: getIds(filter2.collections),
      departments: getIds(filter2.departments),
      workstations: getIds(filter2.workstations),
      employees: getIds(filter2.employees),
    };

    const str1 = JSON.stringify(ids1);
    const str2 = JSON.stringify(ids2);

    return str1 === str2;
  };

  const applyFavoriteFilter = (filterId: string, type: "main" | "default", checked: boolean) => {
    const filter = favoriteFiltersData.find((item) => item.ID.toString() === filterId);
    if (filter) {
      const parsedFilter = JSON.parse(filter.DocumentsFilter);
      const parsedFirstSelectedFilter = parsedFilter.firstSelectedFilter;
      const parsedSubmitFilters = parsedFilter.filters || parsedFilter;
      if (parsedFirstSelectedFilter) {
        changeFirstSelectedFilter(parsedFirstSelectedFilter);
      }
      changeSubmitFilters(parsedSubmitFilters);
      setCurrentFavoriteId(filterId);
      setIsFavorite(true);
      setBundlesModalOpened(false);
    }
  };

  const updateDefaultStatus = (id: string, checked: boolean) => {
    const clickedFilter = favoriteFiltersData.find((item) => item.ID.toString() === id);
    if (!clickedFilter) return;

    if (clickedFilter.isDefault) {
      // Нажали на уже активный дефолт — снимаем
      dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: clickedFilter.ID, isDefault: false } }));
    } else {
      // Снимаем дефолт у всех других, которые isDefault=true
      favoriteFiltersData
        .filter((item) => item.isDefault && item.ID.toString() !== id)
        .forEach((item) => {
          dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: item.ID, isDefault: false } }));
        });

      // Ставим дефолт нажатому
      dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: clickedFilter.ID, isDefault: true } }));
    }
  };

  // Применяем дефолтный фильтр один раз после первой загрузки избранных.
  // Цепочка приоритетов:
  //   1) избранный дефолт
  //   2) коллекции (isCollectionAdmin/Editor) + опционально отдел через зависимости
  //   3) ничего не подошло
  useEffect(() => {
    if (defaultFilterApplied.current) return;
    if (!user.id || favoriteFiltersGetStatus !== "SUCCESS") return;
    // Нужны загруженные подразделения для матчинга user.department в Priority 2
    if (departmentsGetStatus !== "SUCCESS") return;

    const isCurrentFiltersEmpty =
      submitFilters.collections.length === 0 &&
      submitFilters.departments.length === 0 &&
      submitFilters.workstations.length === 0 &&
      submitFilters.employees.length === 0 &&
      submitFilters.query === "";

    if (!isCurrentFiltersEmpty) {
      defaultFilterApplied.current = true;
      setFiltersReady(true);
      return;
    }

    // Priority 1: дефолтный избранный фильтр
    const defaultFilter = favoriteFiltersData.find((item) => item.isDefault);
    if (defaultFilter?.DocumentsFilter) {
      const parsedFilter = JSON.parse(defaultFilter.DocumentsFilter);
      const parsedFirstSelectedFilter = parsedFilter.firstSelectedFilter;
      const parsedSubmitFilters = parsedFilter.filters || parsedFilter;
      if (parsedFirstSelectedFilter) {
        changeFirstSelectedFilter(parsedFirstSelectedFilter);
      }
      changeSubmitFilters(parsedSubmitFilters);
      setCurrentFavoriteId(defaultFilter.ID.toString());
      setIsFavorite(true);
      defaultFilterApplied.current = true;
      setFiltersReady(true);
      return;
    }

    // Priority 2: коллекции пользователя (isCollectionAdmin / isCollectionEditor)
    // Если у пользователя есть отдел — дополнительно проверяем зависимости и добавляем
    // отдел в фильтры только если он связан с коллекциями через dependenciesListName.
    // Отдел без коллекций никогда не применяется самостоятельно.
    const userAdminCollections = getUserAdminCollections(user);
    if (userAdminCollections.length > 0) {
      defaultFilterApplied.current = true;

      (async () => {
        const { items: collectionsResponseData } = await getItemsRequest({
          listName: collectionsListName,
          filters: { ID: userAdminCollections },
        });

        const collections = modifiedItemsState(collectionsResponseData, collectionsListName);
        const collectionItems = collections.slice(0, 1).map((f) => ({
          id: f.ID,
          title: f.Title,
          rest: f,
        }));

        if (collectionItems.length === 0) {
          setFiltersReady(true);
          return;
        }

        // Пробуем подобрать отдел пользователя через зависимости
        let departmentItems: { id: number; title: string; rest: any }[] = [];

        if (user.department) {
          // Находим элементы из списка Departments, чей User_Department содержит строку профиля
          const matchedDepts = (departmentsData as any[]).filter(
            (d) => d.User_Department && d.User_Department.indexOf(user.department) !== -1,
          );

          if (matchedDepts.length > 0) {
            const collectionIds = collectionItems.map((c) => c.id);
            const deptIds = matchedDepts.map((d: any) => d.ID);

            // Проверяем есть ли зависимость между коллекциями и найденными отделами
            const { items: depItems } = await getItemsRequest({
              listName: dependenciesListName,
              filters: { Collection: collectionIds, Department: deptIds },
            });

            if (depItems.length > 0) {
              const depData = modifiedItemsState(depItems, dependenciesListName);
              const validDeptIds = new Set(depData.map((d) => d.Department.ID));

              departmentItems = matchedDepts
                .filter((d: any) => validDeptIds.has(d.ID))
                .map((d: any) => ({ id: d.ID, title: d.Title, rest: d }));
            }
          }
        }

        const newFilters: SubmitWorkPlaceFiltersType = {
          ...submitFilters,
          collections: collectionItems,
          ...(departmentItems.length > 0 && { departments: departmentItems }),
        };

        changeSubmitFilters(newFilters);
        changeFirstSelectedFilter("collections");
        setFiltersReady(true);
      })();
      return;
    }

    // Priority 3: ничего не подошло
    defaultFilterApplied.current = true;
    setFiltersReady(true);
  }, [favoriteFiltersGetStatus, user.id, departmentsGetStatus]);

  useEffect(() => {
    if (!favoriteFiltersData.length) {
      setCurrentFavoriteId(null);
      setIsFavorite(false);
      return;
    }

    // Всегда проверяем совпадение текущих фильтров с сохранёнными
    const matchedFilter = favoriteFiltersData.find((item) => {
      try {
        const parsedFilter = JSON.parse(item.DocumentsFilter);

        const parsedSubmitFilters = parsedFilter.filters || parsedFilter;

        return areFiltersEqual(parsedSubmitFilters, submitFilters);
      } catch {
        return false;
      }
    });

    if (matchedFilter) {
      const wasJustDeleted =
        favoriteFiltersDeleted && matchedFilter.ID.toString() === favoriteFiltersDeleted.toString();
      if (!wasJustDeleted) {
        setCurrentFavoriteId(matchedFilter.ID.toString());
        setIsFavorite(true);
      } else {
        setCurrentFavoriteId(null);
        setIsFavorite(false);
      }
    } else {
      setCurrentFavoriteId(null);
      setIsFavorite(false);
    }
  }, [
    favoriteFiltersUpdatedStatus,
    favoriteFiltersData,
    favoriteFiltersAdded,
    favoriteFiltersDeleted,
    submitFiltersKey,
  ]);

  // Отслеживаем добавление нового фильтра
  useEffect(() => {
    if (favoriteFiltersAdded) {
      // Когда фильтр успешно добавлен, устанавливаем его как избранный
      setCurrentFavoriteId(favoriteFiltersAdded.toString());
      setIsFavorite(true);
      console.log("✅ New filter added:", favoriteFiltersAdded);

      // Обновляем список фильтров из базы данных
      if (user.id) {
        dispatch(
          getItems({
            listName: favoriteFiltersListName,
            filters: { Author: user.id, Type: "workplace" },
          }),
        );
      }
    }
  }, [favoriteFiltersAdded, user.id]);

  // Отслеживаем удаление фильтра
  useEffect(() => {
    if (favoriteFiltersDeleted) {
      console.log("🗑️ Filter deleted:", favoriteFiltersDeleted, "Current:", currentFavoriteId);
      // Если удалённый фильтр был текущим избранным - сбрасываем
      if (currentFavoriteId === favoriteFiltersDeleted.toString()) {
        console.log("❌ Deleting current favorite filter");
        setCurrentFavoriteId(null);
        setIsFavorite(false);
      }

      setBundlesModalOpened(false);
    }
  }, [favoriteFiltersDeleted, user.id]);

  if (bundlesModalOpened) {
    return (
      <BundlesModal
        opened={bundlesModalOpened}
        onApplyFilter={applyFavoriteFilter}
        currentFavoriteId={currentFavoriteId}
        onUpdateDefault={updateDefaultStatus}
      />
    );
  }

  return null;
};
